/**
 * Dynamic URL configuration for BetterAuth across different deployment environments
 *
 * Handles:
 * - Local development (localhost:3000)
 * - Vercel preview deployments (*.vercel.app)
 * - Production (app.arkyv.ai)
 */

import { env } from '@/config/env';

/**
 * Get the base URL for the current environment
 * Priority:
 * 1. BETTER_AUTH_URL (if explicitly set)
 * 2. VERCEL_BRANCH_URL (stable hostname for preview deployments)
 * 3. VERCEL_URL (generated deployment hostname)
 * 4. Fallback to localhost
 */
export function getServerBaseURL(): string {
  if (process.env.APP_ENV === 'test' && process.env.API_BASE_URL) {
    return process.env.API_BASE_URL;
  }

  // Explicit configuration takes precedence
  if (env.BETTER_AUTH_URL) {
    return env.BETTER_AUTH_URL;
  }

  // OAuth callbacks and cookies must share a stable hostname across preview
  // deployments. VERCEL_URL rotates on every deploy, while the branch URL
  // always points at the latest deployment for that branch.
  if (env.VERCEL_ENV === 'preview' && env.VERCEL_BRANCH_URL) {
    return `https://${env.VERCEL_BRANCH_URL}`;
  }

  // For Vercel deployments (preview & production)
  if (env.VERCEL_URL) {
    // VERCEL_URL doesn't include protocol, so add https://
    return `https://${env.VERCEL_URL}`;
  }

  // Local development fallback - use PORT env var if available
  const port = env.PORT || '3000';
  return `http://localhost:${port}`;
}

/**
 * Get the client-side base URL
 * This checks both NEXT_PUBLIC_BETTER_AUTH_URL and runtime detection
 */
export function getClientBaseURL(): string {
  // In browser, use window.location.origin
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }

  // During SSR, use the env var
  if (env.NEXT_PUBLIC_BETTER_AUTH_URL) {
    return env.NEXT_PUBLIC_BETTER_AUTH_URL;
  }

  // Fallback to server base URL for SSR
  return getServerBaseURL();
}

/**
 * Check if current environment is production (app.arkyv.ai)
 */
export function isProductionDomain(): boolean {
  const baseURL = getServerBaseURL();
  return baseURL.includes('arkyv.ai');
}

/**
 * Check if current environment is a Vercel deployment
 */
export function isVercelDeployment(): boolean {
  return Boolean(env.VERCEL_URL);
}

/**
 * Get the appropriate cookie domain for the current environment
 */
export function getCookieDomain(): string | undefined {
  if (isProductionDomain()) {
    return 'arkyv.ai'; // Allows cookies across subdomains
  }
  // For preview deployments and localhost, use host-specific cookies
  return undefined;
}
